import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Search, Zap, BarChart3,
  Settings, LogOut, ExternalLink, ChevronRight, ChevronDown,
  Shield, Building, DollarSign, TrendingUp, MessageSquare,
  Clock, ArrowUpRight, Eye, Phone, Globe, MessageSquarePlus, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import AdminClientManager from './admin/AdminClientManager';
import AdminOnboarding from './admin/AdminOnboarding';
import AdminContentManager from './admin/AdminContentManager';
import AdminGoogleIndexing from './admin/AdminGoogleIndexing';
import AdminUpgradeRequests from './admin/AdminUpgradeRequests';
import AdminReportsDashboard from './admin/AdminReportsDashboard';
import AdminUserManagement from './AdminUserManagement';
import AdminCallTracking from './admin/AdminCallTracking';
import AdminAffiliateManager from './admin/AdminAffiliateManager';
import AdminLeadsManager from './admin/AdminLeadsManager';
import AdminGBPManagement from './admin/AdminGBPManagement';
import AdminGeoSeoPipeline from './admin/AdminGeoSeoPipeline';
import AdminFeedback from './admin/AdminFeedback';
import AdminSeoHealth from './admin/AdminSeoHealth';

const API = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'clients', label: 'Clients', icon: Users },
  { id: 'leads', label: 'Leads (CRM)', icon: MessageSquare },
  { id: 'onboarding', label: 'Onboarding', icon: Building },
  { id: 'blog', label: 'Blog & Content', icon: FileText },
  { id: 'google-indexing', label: 'Google Indexing', icon: Search },
  { id: 'upgrade-requests', label: 'Upgrade Requests', icon: ArrowUpRight },
  { id: 'reports', label: 'Client Reports', icon: BarChart3 },
  { id: 'call-tracking', label: 'Call Tracking', icon: Phone },
  { id: 'gbp-management', label: 'Google Business', icon: Globe },
  { id: 'geo-seo', label: 'Geo SEO', icon: Search },
  { id: 'affiliates', label: 'Partners', icon: Zap },
  { id: 'feedback', label: 'Feedback', icon: MessageSquarePlus },
  { id: 'seo-health', label: 'SEO Health', icon: Activity },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const headers = getAuthHeaders();
    if (!headers) return;
    fetch(`${API}/api/admin/lbp/overview`, { headers })
      .then(r => { if (r.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return null; } return r.json(); })
      .then(d => { if (d) setOverview(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-60'} bg-slate-900 text-white flex-shrink-0 transition-all duration-200 fixed left-0 top-0 h-screen flex flex-col z-50`}>
        <div className="p-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              <span className="font-bold text-sm">LBP Admin</span>
            </div>
          )}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-slate-700 rounded" data-testid="sidebar-toggle">
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        <nav className="p-2 flex-1 overflow-y-auto">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const badge =
              item.id === 'onboarding' && overview?.pending_onboardings > 0 ? overview.pending_onboardings :
              item.id === 'upgrade-requests' && overview?.pending_upgrades > 0 ? overview.pending_upgrades :
              0;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center justify-between p-3 rounded-lg mb-1 transition-colors ${
                  isActive ? 'bg-blue-600 text-white' : 'hover:bg-slate-800 text-slate-300'
                }`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
                </div>
                {!sidebarCollapsed && badge > 0 && (
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5">{badge}</Badge>
                )}
              </button>
            );
          })}
        </nav>

        {!sidebarCollapsed && (
          <div className="p-3 border-t border-slate-700 flex-shrink-0 space-y-2">
            <Button onClick={() => window.open('/', '_blank')} variant="outline" size="sm" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 text-xs" data-testid="view-site-btn">
              <ExternalLink className="h-3 w-3 mr-1" /> View Site
            </Button>
            <Button onClick={logout} variant="outline" size="sm" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 text-xs" data-testid="admin-logout-btn">
              <LogOut className="h-3 w-3 mr-1" /> Logout
            </Button>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-60'} transition-all duration-200`}>
        <header className="bg-white border-b shadow-sm sticky top-0 z-40 px-6 py-4">
          <h1 className="text-lg font-bold text-slate-900" data-testid="page-title">
            {NAV_ITEMS.find(i => i.id === activeTab)?.label || 'Dashboard'}
          </h1>
        </header>

        <div className="p-6">
          {activeTab === 'overview' && <LBPOverview overview={overview} onNavigate={setActiveTab} />}
          {activeTab === 'clients' && <AdminClientManager />}
          {activeTab === 'leads' && <AdminLeadsManager getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'onboarding' && <AdminOnboarding getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'blog' && <AdminContentManager getAuthHeaders={getAuthHeaders} activeTab="blog" />}
          {activeTab === 'google-indexing' && <AdminGoogleIndexing />}
          {activeTab === 'upgrade-requests' && <AdminUpgradeRequests getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'reports' && <AdminReportsDashboard getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'call-tracking' && <AdminCallTracking getHeaders={getAuthHeaders} />}
          {activeTab === 'gbp-management' && <AdminGBPManagement />}
          {activeTab === 'geo-seo' && <AdminGeoSeoPipeline getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'affiliates' && <AdminAffiliateManager activeTab="affiliates" getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'feedback' && <AdminFeedback getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'seo-health' && <AdminSeoHealth getAuthHeaders={getAuthHeaders} />}
          {activeTab === 'settings' && <AdminUserManagement adminToken={localStorage.getItem('admin_token')} />}
        </div>
      </main>
    </div>
  );
}

/* ─── LBP Overview Dashboard ─── */
function LBPOverview({ overview, onNavigate }) {
  if (!overview) return null;
  const o = overview;

  return (
    <div className="space-y-6" data-testid="lbp-overview">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Clients"
          value={o.total_clients}
          subtitle={`${o.active_clients} active`}
          icon={Users}
          color="blue"
          testid="kpi-total-clients"
        />
        <KPICard
          title="Revenue"
          value={`$${o.total_revenue.toLocaleString()}`}
          subtitle={`+$${o.total_sms_revenue.toLocaleString()} SMS`}
          icon={DollarSign}
          color="green"
          testid="kpi-revenue"
        />
        <KPICard
          title="Onboardings"
          value={o.total_onboardings}
          subtitle={`${o.pending_onboardings} pending`}
          icon={Building}
          color="purple"
          testid="kpi-onboardings"
          badge={o.pending_onboardings > 0 ? o.pending_onboardings : null}
        />
        <KPICard
          title="Blog Posts"
          value={o.total_blogs}
          subtitle={`${o.published_blogs} published`}
          icon={FileText}
          color="orange"
          testid="kpi-blogs"
        />
      </div>

      {/* Action cards row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={() => onNavigate('clients')} variant="outline" size="sm" className="w-full justify-start text-left" data-testid="action-manage-clients">
              <Users className="h-4 w-4 mr-2 text-blue-500" /> Manage Clients
            </Button>
            <Button onClick={() => onNavigate('onboarding')} variant="outline" size="sm" className="w-full justify-start text-left" data-testid="action-onboarding">
              <Building className="h-4 w-4 mr-2 text-purple-500" /> View Onboarding Submissions
              {o.pending_onboardings > 0 && <Badge className="ml-auto bg-red-500 text-white text-[10px]">{o.pending_onboardings}</Badge>}
            </Button>
            <Button onClick={() => onNavigate('upgrade-requests')} variant="outline" size="sm" className="w-full justify-start text-left" data-testid="action-upgrades">
              <ArrowUpRight className="h-4 w-4 mr-2 text-amber-500" /> Upgrade Requests
              {o.pending_upgrades > 0 && <Badge className="ml-auto bg-red-500 text-white text-[10px]">{o.pending_upgrades}</Badge>}
            </Button>
            <Button onClick={() => onNavigate('blog')} variant="outline" size="sm" className="w-full justify-start text-left" data-testid="action-blog">
              <FileText className="h-4 w-4 mr-2 text-orange-500" /> Blog Management
            </Button>
          </CardContent>
        </Card>

        {/* Recent Clients */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Recent Clients</CardTitle>
          </CardHeader>
          <CardContent>
            {o.recent_clients?.length > 0 ? (
              <div className="space-y-3">
                {o.recent_clients.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`recent-client-${i}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{c.business_name}</p>
                      <p className="text-xs text-slate-500">{c.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{c.package || 'starter'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No clients yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPICard({ title, value, subtitle, icon: Icon, color, testid, badge }) {
  const colors = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-600 bg-green-50',
    purple: 'text-purple-600 bg-purple-50',
    orange: 'text-orange-600 bg-orange-50',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
          </div>
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="h-5 w-5" />
            {badge && <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] px-1">{badge}</Badge>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default AdminDashboard;
